
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useClientDelete } from "@/hooks/useClientDelete";
import { DeleteClientDialog } from "./DeleteClientDialog";
import type { Client } from "@/types";

interface ClientHeaderProps {
  client: Client;
}

export const ClientHeader = ({ client }: ClientHeaderProps) => {
  const navigate = useNavigate();
  const { isDeleting, error, deleteClient } = useClientDelete();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleDelete = async () => {
    const success = await deleteClient(client.id);
    if (success) {
      setIsDeleteDialogOpen(false);
      // Navigate will happen automatically from the useClientDelete hook
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/clients">
            <Button variant="ghost" size="icon" className="text-white/70 hover:text-white hover:bg-white/10">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-white">Client Profile</h1>
        </div>
        
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-white/70 hover:text-destructive hover:bg-white/10"
          onClick={() => setIsDeleteDialogOpen(true)}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Client
        </Button>
      </div>

      <Card className="backdrop-blur-lg bg-white/10 border-white/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <Avatar className="h-16 w-16">
              {client.avatar && client.avatar !== "/placeholder.svg" && (
                <AvatarImage src={client.avatar} />
              )}
              <AvatarFallback className="bg-white/10 text-white">
                {client.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-medium text-white">{client.name}</h3>
              <p className="text-sm text-white/70">Joined {new Date(client.joinDate).toLocaleDateString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteClientDialog
        clientName={client.name}
        isOpen={isDeleteDialogOpen}
        isDeleting={isDeleting}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        error={error}
      />
    </>
  );
};
